import { devDebug } from "../lib/devLog";
import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, UserPlus, MessageCircle, ThumbsUp, ThumbsDown, Loader2, Bell, CheckCheck, Mic2, Share2, Volume2, VolumeX, ChevronRight } from "lucide-react";
import { useNotifications } from "../hooks/useNotifications";
import { useMiniProfile } from "../lib/miniProfileContext";
import { isDmSoundEnabled, NOTIFICATION_SOUND_EVENT, playDmPing, setDmSoundEnabled } from "../lib/dmSoundPref";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${Math.floor(diff)} sec fa`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
  return `${Math.floor(diff / 86400)} g fa`;
}

const ICONS = {
  follow: { Icon: UserPlus, tone: "bg-emerald-100 text-emerald-700" },
  reply: { Icon: MessageCircle, tone: "bg-sky-100 text-sky-700" },
  like: { Icon: ThumbsUp, tone: "bg-primary/10 text-primary" },
  dislike: { Icon: ThumbsDown, tone: "bg-ink/10 text-ink" },
  new_post: { Icon: Mic2, tone: "bg-violet-100 text-violet-700" },
  share_received: { Icon: Share2, tone: "bg-amber-100 text-amber-700" },
  shared_post: { Icon: Share2, tone: "bg-amber-100 text-amber-700" },
};

const LABEL = {
  follow: "ha iniziato a seguirti",
  reply: "ha risposto al tuo post",
  like: "ha messo mi piace al tuo post",
  dislike: "ha lasciato un feedback negativo al tuo post",
  new_post: "ha pubblicato un nuovo messaggio",
  share_received: "ha diffuso il tuo messaggio",
  shared_post: "ha diffuso un messaggio nella sua rete",
};

const FILTERS = [
  { key: "all", label: "Tutte", types: null },
  { key: "follow", label: "Follow", types: ["follow"] },
  { key: "reply", label: "Risposte", types: ["reply"] },
  { key: "reactions", label: "Reazioni", types: ["like", "dislike"] },
  { key: "publications", label: "Pubblicazioni", types: ["new_post"] },
  { key: "shares", label: "Diffusioni", types: ["share_received", "shared_post"] },
];

function NotificationRow({ n, onOpenAuthor, onOpenNotification }) {
  const conf = ICONS[n.type] || ICONS.reply;
  const { Icon, tone } = conf;
  const unread = !n.read;
  // Le notifiche di risposta/diffusione possono riferirsi al post originale
  // con nomi diversi a seconda della sorgente. Normalizziamo qui il bersaglio
  // senza modificare il payload o gli altri flussi di navigazione.
  const targetPostId = n.type === "reply"
    ? (n.parentPostId || n.postId)
    : (n.originalPostId || n.sharedPostId || n.targetPostId || n.postId || n.post?.id);
  const targetTopicSlug = n.topicSlug || n.post?.topicSlug || n.topic?.slug;
  const canOpenMessage = n.type !== "follow" && Boolean(targetPostId && targetTopicSlug);
  const openTarget = () => {
    if (n.type === "follow" && n.actor?.identityId) {
      onOpenAuthor(n.actor.identityId);
      return;
    }
    if (canOpenMessage) {
      onOpenNotification?.({ ...n, postId: targetPostId, topicSlug: targetTopicSlug });
      return;
    }
    if (n.actor?.identityId) onOpenAuthor(n.actor.identityId);
  };
  const openAuthor = (event) => {
    event.stopPropagation();
    onOpenAuthor(n.actor.identityId);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={openTarget}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openTarget();
        }
      }}
      className={`flex items-center gap-3 py-3 px-2 sm:px-3 border-b border-edgeSoft last:border-b-0 transition-colors cursor-pointer hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${unread ? "bg-primary/5" : ""}`}
      data-testid={`notif-${n.id}`}
      data-read={n.read ? "true" : "false"}
      aria-label={canOpenMessage ? "Apri il messaggio collegato" : `Apri profilo di ${n.actor.displayName}`}
    >
      <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${tone}`}><Icon size={18} strokeWidth={2} /></div>
      <button onClick={openAuthor} className="shrink-0" aria-label={`Apri profilo di ${n.actor.displayName}`} data-testid={`notif-author-${n.actor.identityId}`}>
        {n.actor.avatar ? <img src={n.actor.avatar} alt="" className="w-9 h-9 rounded-full object-cover border border-edge" /> : <div className="w-9 h-9 rounded-full bg-ink text-white flex items-center justify-center text-xs font-semibold">{(n.actor.displayName || "?").slice(0, 1).toUpperCase()}</div>}
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink leading-tight">
          <button onClick={openAuthor} className="font-semibold hover:text-primary hover:underline">{n.actor.displayName}</button>{" "}
          {LABEL[n.type] || "ha interagito con te"}
          {n.topicSlug && <span className="text-muted"> · <span className="font-mono text-[11px]">{n.topicSlug}</span></span>}
        </div>
        <div className="text-[11px] text-muted mt-0.5">{timeAgo(n.createdAt)}</div>
      </div>
      {unread && <span className="w-2 h-2 rounded-full bg-primary shrink-0" aria-label="Non letta" data-testid={`notif-unread-dot-${n.id}`} />}
      <ChevronRight size={17} className="text-muted shrink-0 mr-1" aria-hidden="true" />
    </div>
  );
}

export function NotificationsView({ onBack, onOpenNotification }) {
  const { items, loading, error, refresh, markAllRead, recentCount } = useNotifications();
  const { open } = useMiniProfile();
  const [filter, setFilter] = useState("all");
  const [marking, setMarking] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(isDmSoundEnabled);

  useEffect(() => {
    const syncSoundPreference = () => setSoundEnabled(isDmSoundEnabled());
    window.addEventListener(NOTIFICATION_SOUND_EVENT, syncSoundPreference);
    return () => window.removeEventListener(NOTIFICATION_SOUND_EVENT, syncSoundPreference);
  }, []);

  const toggleSound = () => {
    const next = !soundEnabled;
    setDmSoundEnabled(next);
    setSoundEnabled(next);
    if (next) playDmPing();
  };

  const counts = useMemo(() => {
    const c = { all: items.length, follow: 0, reply: 0, reactions: 0, publications: 0, shares: 0 };
    for (const n of items) {
      if (n.type === "follow") c.follow += 1;
      else if (n.type === "reply") c.reply += 1;
      else if (n.type === "like" || n.type === "dislike") c.reactions += 1;
      else if (n.type === "new_post") c.publications += 1;
      else if (n.type === "share_received" || n.type === "shared_post") c.shares += 1;
    }
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    const rule = FILTERS.find((f) => f.key === filter);
    if (!rule || rule.types == null) return items;
    return items.filter((n) => rule.types.includes(n.type));
  }, [items, filter]);

  const handleMarkAll = async () => {
    if (marking || recentCount === 0) return;
    setMarking(true);
    try { await markAllRead(); }
    catch (err) { devDebug("markAllRead failed", err?.message); }
    finally { setMarking(false); }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="notifications-view">
      <div className="border-b border-edge bg-cream">
        <div className="max-w-[1600px] mx-auto px-6 py-3 flex items-center gap-3 flex-wrap">
          <button onClick={onBack} className="w-9 h-9 rounded-full hover:bg-white flex items-center justify-center" data-testid="notifications-back" aria-label="Torna alla home"><ArrowLeft size={18} /></button>
          <Bell size={18} className="text-ink" /><div className="font-display font-semibold text-lg">Notifiche</div>
          {recentCount > 0 && <span className="text-[11px] font-semibold text-white bg-primary rounded-full px-2 py-0.5" data-testid="notifications-unread-count">{recentCount} nuove</span>}
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={toggleSound}
              className={`text-xs font-semibold inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 transition-colors ${soundEnabled ? "bg-primary text-white border-primary hover:bg-primary/90" : "bg-white text-primary border-primary hover:bg-primary/10"}`}
              data-testid="notifications-sound-toggle"
              aria-pressed={!soundEnabled}
              aria-label={soundEnabled ? "Silenzia notifiche" : "Riattiva audio notifiche"}
            >
              {soundEnabled ? <VolumeX size={14} /> : <Volume2 size={14} />}
              {soundEnabled ? "Silenzia notifiche" : "Riattiva audio"}
            </button>
            <button onClick={handleMarkAll} disabled={marking || recentCount === 0} className="text-xs font-medium inline-flex items-center gap-1.5 rounded-full border border-edge px-3 py-1.5 bg-white text-ink hover:bg-primary hover:text-white hover:border-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-testid="notifications-mark-all-read" aria-label="Segna tutte le notifiche come lette">{marking ? <Loader2 size={13} className="animate-spin" /> : <CheckCheck size={13} />}Segna tutte come lette</button>
            <button onClick={refresh} className="text-xs text-muted hover:text-ink" data-testid="notifications-refresh">Aggiorna</button>
          </div>
        </div>
      </div>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center gap-1 mb-3 overflow-x-auto -mx-1 px-1 pb-1" data-testid="notifications-filters" role="tablist">
          {FILTERS.map((f) => {
            const active = filter === f.key;
            return <button key={f.key} onClick={() => setFilter(f.key)} role="tab" aria-selected={active} className={`text-xs font-medium whitespace-nowrap rounded-full px-3 py-1.5 border transition-colors ${active ? "bg-ink text-white border-ink" : "bg-white text-ink border-edge hover:border-ink"}`} data-testid={`notifications-filter-${f.key}`}>{f.label}<span className={`ml-1.5 text-[10px] ${active ? "text-white/70" : "text-muted"}`}>{counts[f.key]}</span></button>;
          })}
        </div>
        {loading && items.length === 0 && <div className="flex items-center gap-2 text-xs text-muted py-8 justify-center" data-testid="notifications-loading"><Loader2 size={13} className="animate-spin" /> Carico notifiche…</div>}
        {!loading && error && <div className="text-sm text-primary py-6 text-center" data-testid="notifications-error">{error}</div>}
        {!loading && !error && items.length === 0 && <div className="card p-8 text-center text-sm text-muted" data-testid="notifications-empty">Nessuna notifica ancora. Follow, risposte, pubblicazioni, reazioni e diffusioni compariranno qui.</div>}
        {!loading && items.length > 0 && filtered.length === 0 && <div className="card p-8 text-center text-sm text-muted" data-testid="notifications-filter-empty">Nessuna notifica in questa categoria.</div>}
        {!loading && filtered.length > 0 && <div className="card p-1 sm:p-2" data-testid="notifications-list">{filtered.map((n) => <NotificationRow key={n.id} n={n} onOpenAuthor={open} onOpenNotification={onOpenNotification} />)}</div>}
      </div>
    </div>
  );
}
